import type { ScaleMode } from '../core/types';
import type { Projection } from './projection';
import { getBody } from '../core/reference-data';

/**
 * The ONE shared pure marker-radius helper (Story 3.3, AD-3 / AD-10). The renderer
 * adapters call this so they can never diverge on body size. The marker radius is a
 * separate scale factor layered on the shared projection that positions bodies; it is
 * never a second world-to-screen transform.
 *
 * Scale mode is a pure RENDER concern (AD-1): no `core` geometry function, position
 * solver, or orbital-readout selector takes it. Only the drawn marker radius changes
 * between modes; orbital geometry, positions, periods, and selection are identical.
 */

/**
 * Readable planet marker size, in CSS pixels before dpr, at the boot zoom
 * (`READABLE_ZOOM_ANCHOR`). This is Mercury's size at the boot zoom; every other
 * planet is scaled up from it by `READABLE_SIZE_FACTOR` (cube-root of the true
 * physical-radius ratio), so Mercury is the smallest planet and the reference.
 */
export const READABLE_PLANET_BASE_CSS = 4;

/**
 * Readable central-body marker size, in CSS pixels before dpr, at the boot zoom.
 * The central body (the light source / focus) is sized on its own gentle curve
 * rather than the planet physical-radius ladder: the true Sun-to-Mercury radius
 * ratio (~285x) would dwarf the inner system and overlap Mercury at boot, breaking
 * the Story 3.4 boot-framing guarantee. It still scales with zoom and is capped.
 */
export const READABLE_CENTRAL_BASE_CSS = 15;

/**
 * Lower clamp for any readable marker, in CSS pixels before dpr. When fully zoomed
 * out, Mercury settles here, i.e. it is the reference minimum (per the product ask).
 */
export const READABLE_MIN_CSS = 3;

/** Upper clamp for a readable PLANET marker, in CSS pixels before dpr. */
export const READABLE_PLANET_MAX_CSS = 30;

/** Upper clamp for a readable CENTRAL-body marker, in CSS pixels before dpr. */
export const READABLE_CENTRAL_MAX_CSS = 34;

/**
 * The zoom at which the base sizes above apply (marker size grows/shrinks around
 * this anchor). Mirrors `DEFAULT_VIEW_CAMERA.zoom` (the boot camera) so the load
 * view keeps its familiar, boot-framing-safe sizing; kept as a local render-tuning
 * constant to avoid a render->state import for a single number.
 */
export const READABLE_ZOOM_ANCHOR = 6;

/**
 * Fixed-size accessible position marker (locator) radius in CSS pixels (before dpr),
 * drawn in realistic mode so a sub-pixel body stays perceivable (AD-8). It is a
 * fixed device-pixel constant, DECOUPLED from both `physicalRadius` and
 * `collisionRadius`, so it never misrepresents either (AC3). It is a locator, not
 * the body sphere; the renderer draws it shape-distinct (a hollow ring/crosshair).
 */
export const REALISTIC_LOCATOR_RADIUS_CSS = 4;

/** Mercury's true physical radius (metres): the readable planet-size reference. */
const MERCURY_PHYSICAL_RADIUS_METRES = getBody('mercury').physicalRadius;

/**
 * The readable per-body size multiplier relative to Mercury (which returns `1`).
 * Cube-root-compressed real physical-radius ratio: it preserves the size ORDER
 * (Mercury smallest, gas giants larger) while keeping every marker clickable and the
 * Story 6.2/6.4/6.5 sphere detail legible, instead of the extreme true ratios
 * (Jupiter ~29x, the Sun ~285x). Non-positive/unknown radii fall back to `1`.
 */
export function readableSizeFactor(physicalRadiusMetres: number): number {
  if (!(physicalRadiusMetres > 0)) return 1;
  return Math.cbrt(physicalRadiusMetres / MERCURY_PHYSICAL_RADIUS_METRES);
}

/**
 * Gentle zoom response: `sqrt(zoom / anchor)`, so marker size changes "a bit" with
 * zoom (1 at the boot anchor, smaller when zoomed out, larger when zoomed in) and
 * stays inside the clamps across the whole zoom range. Non-positive zoom yields 0,
 * which the clamp turns into the floor.
 */
export function readableZoomResponse(zoom: number): number {
  return Math.sqrt(Math.max(zoom, 0) / READABLE_ZOOM_ANCHOR);
}

function clampReadableCss(rawCss: number, maxCss: number): number {
  return Math.min(maxCss, Math.max(READABLE_MIN_CSS, rawCss));
}

/**
 * The drawn body-marker radius in DEVICE pixels for the given scale mode.
 *
 * - `'readable'`: an exaggerated, easy-to-see size that (a) ranks bodies by their
 *   true physical radius through a cube-root-compressed factor (Mercury is the
 *   reference/smallest planet), (b) responds gently to `zoom` (grows when zoomed in,
 *   shrinks toward the Mercury floor when zoomed out), and (c) is clamped to
 *   [`READABLE_MIN_CSS`, planet/central max]. The central body uses its own base and
 *   cap (not the physical ladder) so it never dwarfs the inner system at boot.
 *   Independent of the projection: the shared `zoom` multiplier drives the response,
 *   not the projection's absolute length scale, so it is Vitest-testable headless.
 * - `'realistic'`: the body's TRUE physical radius mapped through the SAME length
 *   scale the projection uses for orbital distances (AD-10). Planets are sub-pixel
 *   at fit-all BY DESIGN (space is mostly empty); this is the teaching contrast,
 *   not a bug, and is NOT clamped to a minimum (which would break the true ratio).
 *
 * Both branches return DEVICE pixels: the readable sizes are multiplied by `dpr`,
 * and `projection.lengthToScreen` already folds in dpr. `zoom` is the session VIEW
 * zoom multiplier (`state.camera.zoom`; `1` = fit-all), used only by the readable
 * branch. Pure (no canvas, no state) so it is Vitest-testable headless.
 */
export function markerRadiusPx(
  mode: ScaleMode,
  physicalRadiusMetres: number,
  isCentral: boolean,
  projection: Projection,
  dpr: number,
  zoom: number
): number {
  if (mode === 'readable') {
    const response = readableZoomResponse(zoom);
    if (isCentral) {
      const rawCss = READABLE_CENTRAL_BASE_CSS * response;
      return clampReadableCss(rawCss, READABLE_CENTRAL_MAX_CSS) * dpr;
    }
    const rawCss = READABLE_PLANET_BASE_CSS * readableSizeFactor(physicalRadiusMetres) * response;
    return clampReadableCss(rawCss, READABLE_PLANET_MAX_CSS) * dpr;
  }
  // Realistic: true physical radius through the shared projection length scale.
  return projection.lengthToScreen(physicalRadiusMetres);
}
